import { type MouseEventHandler, type ReactNode, useState } from 'react';

import Icon from 'components/Icon';
import PlainButton from 'components/PlainButton';
import { FONT_CLASSES } from 'components/Text';
import { useId } from 'hooks';
import type { HTMLAttributes, HTMLTagName } from 'types';
import { cn, cnm } from 'utils/classNames';

export type AccordionColor = 'positive' | 'red' | 'white' | 'white-red';
export type AccordionSize = 'heading' | 'large' | 'medium' | 'small';

interface BaseProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
	/** Content to show/hide. */
	children: ReactNode;

	/** Additional classes for the outermost container. */
	className?: string;

	/** Color scheme. */
	color?: AccordionColor;

	/** Additional classes for the content container. */
	contentClassName?: string;

	/** A unique ID, will be set on the trigger button. */
	id?: string;

	/** Set to false to disable all accordion behavior. */
	isActive?: boolean;

	/** Size of text and padding. */
	size?: AccordionSize;

	/** Trigger button text. */
	title: string;

	/** Additional classes for the trigger button container. */
	titleClassName?: string;

	/** Tag for the trigger button container. */
	titleTag?: HTMLTagName;
}

interface PropsWithState extends BaseProps {
	initialOpen?: never;
	isOpen: boolean;
	/** Trigger click handler to toggle the `isOpen` state. */
	onClick: MouseEventHandler<HTMLButtonElement>;
}

interface PropsWithoutState extends BaseProps {
	/** Initial open state when uncontrolled. */
	initialOpen?: boolean;
	isOpen?: never;
	onClick?: never;
}

type Props = PropsWithState | PropsWithoutState;

/**
 * Toggle showing/hiding content.
 *
 * Follows the accordion pattern.
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */
export default function Accordion({
	children,
	className,
	color = 'white-red',
	contentClassName,
	id,
	initialOpen = false,
	isActive = true,
	isOpen: isOpenProp,
	onClick,
	size = 'heading',
	title,
	titleClassName,
	titleTag: TitleComponent = 'h3',
	...attrs
}: Props) {
	const [internalIsOpen, setInternalIsOpen] = useState<boolean>(initialOpen);
	// Behave like inputs where it can be controlled or uncontrolled.
	const isOpen = onClick ? isOpenProp : internalIsOpen;
	const fallbackId = useId('accordion');
	const triggerId = id || fallbackId;
	const contentId = `${triggerId}-content`;
	const isPaddedColor = color === 'red' || color === 'positive';

	return (
		<div
			{...attrs}
			className={cnm(
				isActive && [
					color === 'positive' && 'bg-positive text-negative',
					color === 'red' && 'bg-brand text-negative',
					size !== 'small' && [
						'border-y border-solid',
						// Remove double border for consecutive accordions.
						'accordion peer peer-[.accordion]:border-t-0',
						color === 'red' && 'border-brandDecorative',
						color === 'positive' && 'border-opacityLighten',
						color === 'white-red' && 'border-strong',
						color === 'white' && 'border-subtle',
					],
				],
				className,
			)}
		>
			<TitleComponent
				className={cnm(
					size === 'small' && 'text-sm',
					size === 'medium' && 'text-md font-bold',
					size === 'large' && 'text-lg font-bold',
					size !== 'heading' && 'font-body',
					size === 'heading' && FONT_CLASSES.h3,
					titleClassName,
				)}
			>
				<span hidden={isActive}>{title}</span>
				<PlainButton
					id={triggerId}
					aria-controls={contentId}
					aria-expanded={isOpen}
					hidden={!isActive}
					onClick={(e) => {
						if (onClick) {
							onClick(e);
						} else {
							setInternalIsOpen((current) => !current);
						}
					}}
					className={cn(
						'group/accordion flex w-full items-center justify-between text-left',
						size === 'small' && ['py-2', isPaddedColor && 'px-2'],
						size === 'medium' && ['py-3', isPaddedColor && 'px-3'],
						(size === 'large' || size === 'heading') && [
							'py-4',
							isPaddedColor && 'px-4',
						],
					)}
				>
					{title}
					<span
						className={cn(
							'ml-4 inline-flex size-8 shrink-0 items-center justify-center rounded-full group-hover/accordion:forced-colors-outline',
							color === 'white' && 'group-hover/accordion:bg-baseHover',
							(color === 'white-red' || color === 'red') &&
								'bg-brand text-negative group-hover/accordion:bg-brandHover',
							color === 'positive' &&
								'bg-positive text-negative group-hover/accordion:bg-opacityLighten',
						)}
					>
						<Icon
							name="chevron-down"
							className={cn('transition-transform', isOpen && 'rotate-180')}
						/>
					</span>
				</PlainButton>
			</TitleComponent>
			<div
				id={contentId}
				role={isActive ? 'region' : undefined}
				aria-labelledby={isActive ? triggerId : undefined}
				hidden={isActive && !isOpen}
				className={cnm(
					isActive && [
						size === 'small' && isPaddedColor && 'px-2 pb-2',
						size === 'medium' && ['pb-3', isPaddedColor && 'px-3'],
						(size === 'large' || size === 'heading') && [
							'pb-4',
							isPaddedColor && 'px-4',
						],
					],
					contentClassName,
				)}
			>
				{children}
			</div>
		</div>
	);
}
Accordion.displayName = 'Accordion';
